import React from 'react';
import camelCase from 'lodash/camelCase';
import invert from 'lodash/invert';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { RequestStatusCount } from 'src/components/RequestStatusCount';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/system';
import { useTranslate } from '@im/react-localization';
import {
  ExceptionDealStatsResponse,
  EDStatus,
  EDStatusCode,
  ExceptionBidStatus,
} from '../hooks/exceptionDeal';
import BusinessTypeTabs from '../components/BusinessTypeTabs/BusinessTypeTabs';
import { IM_PRICING_GDE_UI_SUBSCRIPTIONDEALS_ENABLED } from 'src/util/constants';
import { useFeatureFlagEnabled } from 'src/hooks/useFeatureFlagEnabled';

export function useStatusCards({
  activeFilters,
  counts,
  isAdmin,
  onClick,
}: any) {
  const theme = useTheme();
  const translate = useTranslate();
  const statusCards = isAdmin
    ? {
        [EDStatus.ESCALATED]: theme.palette.error[600],
        [EDStatus.PENDING_REVIEW]: theme.palette.purple[700],
        [EDStatus.NEW_RESELLER]: theme.palette.primary[900],
      }
    : {
        [EDStatus.ESCALATED]: theme.palette.error[600],
        [EDStatus.REJECTED]: theme.palette.warning[700],
        [EDStatus.NEW_RESELLER]: theme.palette.purple[700],
        [EDStatus.PENDING_REVIEW]: theme.palette.success[900],
      };

  return Object.entries(statusCards).map(([status, color]) => ({
    color,
    count:
      counts?.[`${camelCase(invert(EDStatus)[status])}count`.toLowerCase()] ??
      0,
    id: status,
    isActive: activeFilters
      .split(',')
      .includes(String(EDStatusCode[status as ExceptionBidStatus])),
    onTabClick: () => onClick(status),
    text: translate(
      `ExceptionDeals.Status${isAdmin ? 'Admin' : 'Other'}${
        invert(EDStatus)[status]
      }`
    ),
  }));
}

export default function EDListPageHeader(
  props: Readonly<{
    businessType?: string | null;
    data: ExceptionDealStatsResponse | undefined;
    filters: string;
    isLoading?: boolean;
    onTabChange: (x: string) => void;
    role: boolean;
    setFilters: React.Dispatch<React.SetStateAction<string>>;
  }>
) {
  const role = props.role;
  const translate = useTranslate();
  const isSubscriptionEnabled = useFeatureFlagEnabled(
    IM_PRICING_GDE_UI_SUBSCRIPTIONDEALS_ENABLED
  );

  const handleOnStatusCardClick = (status: string) => {
    if (
      props.filters
        .split(',')
        .includes(String(EDStatusCode[status as keyof typeof EDStatusCode]))
    )
      props.setFilters(
        props.filters
          .split(',')
          .filter(
            (n) =>
              n !== String(EDStatusCode[status as keyof typeof EDStatusCode])
          )
          .toString()
      );
    else
      props.setFilters(
        String(EDStatusCode[status as keyof typeof EDStatusCode])
      );
  };

  const statusCards = useStatusCards({
    activeFilters: props.filters,
    counts: props.data,
    isAdmin: role,
    onClick: handleOnStatusCardClick,
  });

  return (
    <Box>
      <Box mb={2}>
        <Typography data-testid="vp-ed-header-text" fontSize={22} variant="h2">
          {translate('VendorProgram.Exceptiondeals')}
        </Typography>
      </Box>
      {isSubscriptionEnabled && (
        <BusinessTypeTabs
          businessType={
            props.businessType === 'Subscription' ? 'Subscription' : ''
          }
          onTabChange={props.onTabChange}
        />
      )}
      {props.businessType !== 'Subscription' && (
        <Stack
          direction="row"
          flex={1}
          flexWrap="wrap"
          fontWeight={2}
          gap={2}
          mb={2}
          sx={{ '& > div': { flex: 1 } }}
        >
          {statusCards.map((status) => {
            return (
              <RequestStatusCount
                color={status.color}
                count={status.count}
                dataTestId={`vp-ed-status-card-${status.text}`}
                isActive={status.isActive}
                isLoading={props.isLoading}
                key={status.id}
                onClick={status.onTabClick}
                text={status.text}
              />
            );
          })}
        </Stack>
      )}
    </Box>
  );
}
